"use client"

import { CheckCircle2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { nowFor, nowProgress, setNowDone } from "@/lib/breach/now-actions"
import type { Workspace } from "@/lib/breach/workspace"
import type { GoTo } from "./incident-nav"
import type { WorkspaceUpdater } from "./incident-tools"
import { ResourceLink } from "./resource-link"

const timeOf = (iso?: string) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "")

/** The few things that matter most in the first hours, at the top of the plan. Ticking one also ticks its step item and logs the time. */
export function NowPanel({ w, update, go }: { w: Workspace; update: WorkspaceUpdater; go: GoTo }) {
  const list = nowFor(w)
  const { done, total } = nowProgress(w)
  if (total === 0) return null
  const all = done === total

  return (
    <section className="rounded-xl border-2 border-accent/60 bg-card p-4 sm:p-5" aria-labelledby="now-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="now-heading" className="text-xl font-bold">Do these now</h2>
        <p className="text-sm text-muted-foreground" aria-live="polite">{done} of {total} done</p>
      </div>
      <div className="mt-2 h-2 rounded-full bg-muted" role="progressbar" aria-valuenow={done} aria-valuemin={0} aria-valuemax={total} aria-label="Urgent actions done">
        <div className="h-2 rounded-full bg-accent-solid transition-all" style={{ width: `${(done / total) * 100}%` }} />
      </div>
      <p className="mt-2 text-sm text-muted-foreground">Tick each one when it&apos;s done. The time goes on your Timeline.</p>

      <ul className="mt-3 grid gap-2">
        {list.map((r) => {
          const id = `now-${r.action.id}`
          return (
            <li key={r.action.id} className={cn("flex items-start gap-3 rounded-lg border p-3", r.done ? "border-border bg-background/40" : "border-border")}>
              <input id={id} type="checkbox" className="mt-1 h-5 w-5 shrink-0" checked={r.done}
                onChange={(e) => update((x) => setNowDone(x, r, e.target.checked))} />
              <div className="grid flex-1 gap-0.5">
                <label htmlFor={id} className={cn("text-base font-medium", r.done && "text-muted-foreground line-through")}>{r.action.label}</label>
                {r.action.detail && !r.done && <p className="text-sm text-muted-foreground">{r.action.detail}</p>}
                {r.done && r.doneAt && <p className="text-xs text-muted-foreground">Done at {timeOf(r.doneAt)}</p>}
                {!r.done && (
                  <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm print:hidden">
                    {r.action.link && <ResourceLink href={r.action.link.href} className="underline">{r.action.link.label}</ResourceLink>}
                    <button type="button" className="underline" onClick={() => go({ v: "step", step: r.action.step, p: 1 })}>How</button>
                  </p>
                )}
              </div>
            </li>
          )
        })}
      </ul>

      {all && (
        <p className="mt-3 flex items-start gap-2 rounded-lg border border-border bg-background/60 p-3 text-sm" role="status">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-500" aria-hidden />
          <span>Those urgent actions are done. Keep going with the steps below, and keep your Timeline up to date.</span>
        </p>
      )}
    </section>
  )
}

/** A slim reminder on the other screens, so the urgent list is never out of sight. Hidden once everything is done. */
export function NowBar({ w, go }: { w: Workspace; go: GoTo }) {
  const { done, total } = nowProgress(w)
  if (total === 0 || done === total) return null
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-accent/60 bg-card px-3 py-2 text-sm print:hidden">
      <span><strong>Do these now:</strong> {done} of {total} done</span>
      <Button size="sm" variant="outline" onClick={() => go({ v: "index" })}>Show the list</Button>
    </div>
  )
}
